// Create modals for the Calendar page (and reusable from record pages):
// EventModal → ScheduledEvent (CRM meeting/site visit/…), KeyDateModal →
// KeyDate. Both persist through the store's write-through (app_entities).

import { useEffect, useMemo, useState } from 'react'
import { Modal } from './Modal'
import { Field, TextInput, Select } from './Field'
import { Button } from './Primitives'
import { useStore } from '@/store/useStore'
import { useCurrentUser } from '@/auth/useCurrentUser'
import { daysUntil } from '@/lib/format'
import type { EntityRef, KeyDate, KeyDateType, ScheduledEvent, ScheduledEventType } from '@/types/entities'

const EVENT_TYPES: ScheduledEventType[] = [
  'Sales Meeting', 'Performance Review', 'Reporting Window', 'Site Visit', 'Hearing', 'Deadline', 'Internal',
]
const KD_TYPES: KeyDateType[] = [
  'Reporting Window', 'Refi', 'Loan Maturity', 'Valuation', 'Covenant', 'Contract Renewal',
  'Budget', 'Insurance', 'Hard Money', 'DD Expiration', 'Extension', 'Hearing', 'Permit Expiration', 'Stabilization',
]

const newId = (prefix: string) =>
  `${prefix}-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`

/** Account/property target picker options (shared by both modals). */
function useTargets() {
  const data = useStore((s) => s.data)
  return useMemo(() => {
    const accounts = data.accounts
      .filter((a) => a.type !== 'Personal' && !a.archived)
      .map((a) => ({ key: `account:${a.id}`, label: a.name, group: 'Accounts' }))
    const properties = [...data.properties]
      .filter((p) => !p.archived)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((p) => ({ key: `property:${p.id}`, label: p.name, group: 'Properties' }))
    return [...accounts, ...properties]
  }, [data])
}

function resolveTarget(data: ReturnType<typeof useStore.getState>['data'], key: string): EntityRef | undefined {
  if (!key) return undefined
  const [kind, id] = key.split(/:(.+)/)
  const label = kind === 'account'
    ? data.accounts.find((a) => a.id === id)?.name
    : data.properties.find((p) => p.id === id)?.name
  return { kind: kind as EntityRef['kind'], id, label }
}

function TargetSelect({ value, onChange, required }: { value: string; onChange: (v: string) => void; required?: boolean }) {
  const targets = useTargets()
  return (
    <Field label="Linked to" required={required} hint="The account or property this belongs to.">
      <Select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">{required ? 'Choose a record…' : 'None (general)'}</option>
        <optgroup label="Accounts">
          {targets.filter((t) => t.group === 'Accounts').map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </optgroup>
        <optgroup label="Properties">
          {targets.filter((t) => t.group === 'Properties').map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </optgroup>
      </Select>
    </Field>
  )
}

export function EventModal({ open, onClose, attachTo }: { open: boolean; onClose: () => void; attachTo?: EntityRef }) {
  const upsert = useStore((s) => s.upsert)
  const data = useStore((s) => s.data)
  const user = useCurrentUser()
  const [title, setTitle] = useState('')
  const [type, setType] = useState<ScheduledEventType>('Sales Meeting')
  const [day, setDay] = useState('')
  const [time, setTime] = useState('09:00')
  const [location, setLocation] = useState('')
  const [attendees, setAttendees] = useState('')
  const [targetKey, setTargetKey] = useState('')
  useEffect(() => {
    if (open) { setTitle(''); setType('Sales Meeting'); setDay(''); setTime('09:00'); setLocation(''); setAttendees(''); setTargetKey('') }
  }, [open])

  const save = () => {
    const ev: ScheduledEvent = {
      id: newId('evt'),
      title: title.trim(),
      type,
      start: `${day}T${time || '09:00'}:00`,
      attendees: attendees.split(',').map((a) => a.trim()).filter(Boolean),
      location: location.trim() || undefined,
      attachedTo: attachTo ?? resolveTarget(data, targetKey),
    }
    upsert('events', ev)
    onClose()
  }
  return (
    <Modal open={open} onClose={onClose} title="New meeting / event" footer={
      <><Button variant="default" onClick={onClose}>Cancel</Button>
        <Button variant="primary" disabled={!title.trim() || !day} onClick={save}>Add to calendar</Button></>
    }>
      <Field label="Title" required><TextInput value={title} onChange={(e) => setTitle(e.target.value)} autoFocus placeholder="Q3 portfolio review" /></Field>
      <Field label="Type">
        <Select value={type} onChange={(e) => setType(e.target.value as ScheduledEventType)}>
          {EVENT_TYPES.map((t) => <option key={t}>{t}</option>)}
        </Select>
      </Field>
      <Field label="Date" required><TextInput type="date" value={day} onChange={(e) => setDay(e.target.value)} /></Field>
      <Field label="Time"><TextInput type="time" value={time} onChange={(e) => setTime(e.target.value)} /></Field>
      <Field label="Location"><TextInput value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Microsoft Teams / office / site" /></Field>
      <Field label="Attendees" hint={`Comma-separated. You (${user.displayName}) are implied.`}>
        <TextInput value={attendees} onChange={(e) => setAttendees(e.target.value)} placeholder="Robert Masterson, Lindsay Forrester" />
      </Field>
      {!attachTo && <TargetSelect value={targetKey} onChange={setTargetKey} />}
    </Modal>
  )
}

export function KeyDateModal({ open, onClose, attachTo }: { open: boolean; onClose: () => void; attachTo?: EntityRef }) {
  const upsert = useStore((s) => s.upsert)
  const data = useStore((s) => s.data)
  const user = useCurrentUser()
  const [title, setTitle] = useState('')
  const [type, setType] = useState<KeyDateType>('Reporting Window')
  const [day, setDay] = useState('')
  const [owner, setOwner] = useState('')
  const [targetKey, setTargetKey] = useState('')
  useEffect(() => {
    if (open) { setTitle(''); setType('Reporting Window'); setDay(''); setOwner(user.displayName); setTargetKey('') }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const save = () => {
    const ref = attachTo ?? resolveTarget(data, targetKey)
    if (!ref) return
    const du = daysUntil(day)
    const kd: KeyDate = {
      id: newId('kd'),
      title: title.trim(),
      type,
      date: day,
      status: du != null && du < 0 ? 'Overdue' : du != null && du <= 14 ? 'Due Soon' : 'Upcoming',
      attachedTo: ref,
      owner: owner.trim() || undefined,
    }
    upsert('keyDates', kd)
    onClose()
  }
  return (
    <Modal open={open} onClose={onClose} title="New critical date" footer={
      <><Button variant="default" onClick={onClose}>Cancel</Button>
        <Button variant="primary" disabled={!title.trim() || !day || (!attachTo && !targetKey)} onClick={save}>Add critical date</Button></>
    }>
      <Field label="Title" required><TextInput value={title} onChange={(e) => setTitle(e.target.value)} autoFocus placeholder="Q3 JPM reporting window" /></Field>
      <Field label="Type">
        <Select value={type} onChange={(e) => setType(e.target.value as KeyDateType)}>
          {KD_TYPES.map((t) => <option key={t}>{t}</option>)}
        </Select>
      </Field>
      <Field label="Date" required><TextInput type="date" value={day} onChange={(e) => setDay(e.target.value)} /></Field>
      <Field label="Owner"><TextInput value={owner} onChange={(e) => setOwner(e.target.value)} /></Field>
      {!attachTo && <TargetSelect value={targetKey} onChange={setTargetKey} required />}
    </Modal>
  )
}
